import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  Loader2,
  Plane,
  AlertTriangle,
  Wrench,
  CheckCircle,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api';
import { API_ROUTES, Role } from '@avgrp/shared';
import { useAuth } from '@/hooks/useAuth';
import { hasMinRole } from '@/utils/roles';

// ─── Types ──────────────────────────────────────────────────

type AircraftStatus = 'ACTIVE' | 'MAINTENANCE' | 'RETIRED';

interface AircraftType {
  id: string;
  icaoCode: string;
  iataCode: string | null;
  name: string;
}

interface Aircraft {
  id: string;
  registration: string;
  aircraftTypeId: string;
  organizationId: string;
  status: AircraftStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  aircraftType: AircraftType;
  _count?: { flights: number };
}

interface AircraftForm {
  registration: string;
  aircraftTypeId: string;
  status: AircraftStatus;
  notes: string;
}

const emptyForm: AircraftForm = {
  registration: '',
  aircraftTypeId: '',
  status: 'ACTIVE',
  notes: '',
};

// ─── Status config ──────────────────────────────────────────

const statusColors: Record<AircraftStatus, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700',
  MAINTENANCE: 'bg-amber-50 text-amber-700',
  RETIRED: 'bg-gray-100 text-gray-600',
};

const statusIcons: Record<AircraftStatus, React.ElementType> = {
  ACTIVE: CheckCircle,
  MAINTENANCE: Wrench,
  RETIRED: AlertTriangle,
};

const statusLabels: Record<AircraftStatus, string> = {
  ACTIVE: 'Active',
  MAINTENANCE: 'Maintenance',
  RETIRED: 'Retired',
};

// ─── Component ──────────────────────────────────────────────

export function FleetPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingAircraft, setEditingAircraft] = useState<Aircraft | null>(null);
  const [formData, setFormData] = useState<AircraftForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // ─── Queries ────────────────────────────────────────────────

  const { data: aircraft, isLoading } = useQuery({
    queryKey: ['aircraft', statusFilter],
    queryFn: () =>
      apiGet<{ data: Aircraft[] }>(`${API_ROUTES.RESOURCES}/aircraft`, {
        params: { status: statusFilter || undefined },
      }),
    select: (res) => res.data,
  });

  const { data: aircraftTypes } = useQuery({
    queryKey: ['aircraft-types'],
    queryFn: () =>
      apiGet<{ data: AircraftType[] }>(`${API_ROUTES.RESOURCES}/aircraft-types`),
    select: (res) => res.data,
  });

  // ─── Mutations ──────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: AircraftForm) =>
      apiPost(`${API_ROUTES.RESOURCES}/aircraft`, {
        ...data,
        organizationId: user?.organizationId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      closeForm();
    },
    onError: (err: any) => {
      setFormErrors({
        general: err?.response?.data?.error?.message || err?.message || 'Failed to create aircraft',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AircraftForm> }) =>
      apiPut(`${API_ROUTES.RESOURCES}/aircraft/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      closeForm();
    },
    onError: (err: any) => {
      setFormErrors({
        general: err?.response?.data?.error?.message || err?.message || 'Failed to update aircraft',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`${API_ROUTES.RESOURCES}/aircraft/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aircraft'] });
    },
  });

  // ─── Helpers ──────────────────────────────────────────────

  const aircraftList = aircraft ?? [];
  const typesList = aircraftTypes ?? [];

  const filteredAircraft = aircraftList.filter((ac) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        ac.registration.toLowerCase().includes(q) ||
        ac.aircraftType?.name?.toLowerCase().includes(q) ||
        ac.aircraftType?.icaoCode?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Stats (computed from full list, not filtered)
  const totalCount = aircraftList.length;
  const activeCount = aircraftList.filter((ac) => ac.status === 'ACTIVE').length;
  const maintenanceCount = aircraftList.filter((ac) => ac.status === 'MAINTENANCE').length;
  const retiredCount = aircraftList.filter((ac) => ac.status === 'RETIRED').length;

  function openCreate() {
    setEditingAircraft(null);
    setFormData(emptyForm);
    setFormErrors({});
    setShowForm(true);
  }

  function openEdit(ac: Aircraft) {
    setEditingAircraft(ac);
    setFormData({
      registration: ac.registration,
      aircraftTypeId: ac.aircraftTypeId,
      status: ac.status,
      notes: ac.notes || '',
    });
    setFormErrors({});
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingAircraft(null);
    setFormData(emptyForm);
    setFormErrors({});
  }

  function handleSave() {
    const errors: Record<string, string> = {};
    if (!editingAircraft && !formData.registration.trim()) errors.registration = 'Required';
    if (!formData.aircraftTypeId) errors.aircraftTypeId = 'Required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (editingAircraft) {
      const { registration: _reg, ...updateData } = formData;
      updateMutation.mutate({ id: editingAircraft.id, data: updateData });
    } else {
      createMutation.mutate({
        ...formData,
        registration: formData.registration.toUpperCase().trim(),
      });
    }
  }

  function handleDelete(ac: Aircraft) {
    if (window.confirm(`Delete aircraft ${ac.registration}? This will fail if the aircraft has flights.`)) {
      deleteMutation.mutate(ac.id);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fleet Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage individual aircraft in your fleet
          </p>
        </div>
        {hasMinRole(user?.role, Role.MANAGER) && (
          <button onClick={openCreate} className="btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            Add Aircraft
          </button>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="card flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-aviation-50">
            <Plane className="h-5 w-5 text-aviation-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            <p className="text-xs text-gray-500">Total Aircraft</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-700">{activeCount}</p>
            <p className="text-xs text-gray-500">Active</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
            <Wrench className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-700">{maintenanceCount}</p>
            <p className="text-xs text-gray-500">In Maintenance</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
            <AlertTriangle className="h-5 w-5 text-gray-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-600">{retiredCount}</p>
            <p className="text-xs text-gray-500">Retired</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by registration, type name, or ICAO code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="MAINTENANCE">Maintenance</option>
          <option value="RETIRED">Retired</option>
        </select>
      </div>

      {/* Create/Edit form */}
      {hasMinRole(user?.role, Role.MANAGER) && showForm && (
        <div className="card border-aviation-200 bg-aviation-50/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingAircraft ? 'Edit Aircraft' : 'New Aircraft'}
            </h3>
            <button onClick={closeForm} className="rounded-lg p-1 text-gray-400 hover:bg-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {formErrors.general && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {formErrors.general}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Registration */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Registration <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.registration}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, registration: e.target.value.toUpperCase() }))
                }
                placeholder="N12345"
                disabled={!!editingAircraft}
                className={`input-field font-mono uppercase ${
                  editingAircraft ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''
                } ${formErrors.registration ? 'border-red-300' : ''}`}
              />
              {formErrors.registration && (
                <p className="mt-0.5 text-xs text-red-500">{formErrors.registration}</p>
              )}
            </div>

            {/* Aircraft Type */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Aircraft Type <span className="text-red-400">*</span>
              </label>
              <select
                value={formData.aircraftTypeId}
                onChange={(e) => setFormData((p) => ({ ...p, aircraftTypeId: e.target.value }))}
                className={`input-field ${formErrors.aircraftTypeId ? 'border-red-300' : ''}`}
              >
                <option value="">Select type...</option>
                {typesList.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.icaoCode})
                  </option>
                ))}
              </select>
              {formErrors.aircraftTypeId && (
                <p className="mt-0.5 text-xs text-red-500">{formErrors.aircraftTypeId}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, status: e.target.value as AircraftStatus }))
                }
                className="input-field"
              >
                <option value="ACTIVE">Active</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="RETIRED">Retired</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={1}
                className="input-field resize-none"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={closeForm} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isPending}
              className="btn-primary"
            >
              {isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {editingAircraft ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Aircraft table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
          </div>
        ) : filteredAircraft.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Plane className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm text-gray-500">
              {searchQuery || statusFilter
                ? 'No aircraft match your filter'
                : 'No aircraft in the fleet yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Registration</th>
                  <th className="table-header">Aircraft Type</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Notes</th>
                  <th className="table-header">Flights</th>
                  {hasMinRole(user?.role, Role.MANAGER) && (
                    <th className="table-header">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredAircraft.map((ac) => {
                  const StatusIcon = statusIcons[ac.status];
                  return (
                    <tr
                      key={ac.id}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                    >
                      <td className="table-cell font-mono font-bold text-gray-900">
                        {ac.registration}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">
                            {ac.aircraftType?.name}
                          </span>
                          {(ac.aircraftType?.iataCode || ac.aircraftType?.icaoCode) && (
                            <span className="badge bg-gray-100 text-gray-600">
                              {ac.aircraftType?.iataCode || ac.aircraftType?.icaoCode}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${statusColors[ac.status]}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {statusLabels[ac.status]}
                        </span>
                      </td>
                      <td className="table-cell max-w-[200px] truncate text-gray-500">
                        {ac.notes || '-'}
                      </td>
                      <td className="table-cell">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {ac._count?.flights ?? 0}
                        </span>
                      </td>
                      {hasMinRole(user?.role, Role.MANAGER) && (
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(ac)}
                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            title="Edit aircraft"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(ac)}
                            disabled={deleteMutation.isPending}
                            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            title="Delete aircraft"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete error */}
      {deleteMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {(deleteMutation.error as any)?.response?.data?.error?.message
            || (deleteMutation.error as any)?.message
            || 'Failed to delete aircraft. It may have associated flights — consider retiring it instead.'}
        </div>
      )}
    </div>
  );
}
